import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  exportDefect,
  fetchAssignedDefects,
  fetchAllProjectDefects,
  fetchDefectById,
  fetchDefectFolderTree,
  fetchDefectLogs,
  fetchDefectReferences,
  fetchLinkedTestCases,
} from "./defectlogs.api";

import { Defect } from "@/common/models/defect.model";

interface QueryParams {
  versionId: number;

  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}
export const useAllDefects = (
  versionId: number,
  options?: { enabled?: boolean },
) =>
  useInfiniteQuery({
    queryKey: ["defectLogs", versionId],
    queryFn: ({ pageParam = 0 }) => fetchDefectLogs(pageParam, versionId),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!versionId && (options?.enabled ?? true),
  });

export const useProjectDefects = (options?: { enabled?: boolean }) =>
  useInfiniteQuery({
    queryKey: ["projectDefectLogs"],
    queryFn: ({ pageParam = 0 }) => fetchAllProjectDefects(pageParam),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
  });

// export const useAllDefects = (params: Omit<QueryParams, "page">, options?: { enabled?: boolean }) =>
//   useInfiniteQuery({
//      queryKey: ["defectLogs", versionId],
//      queryFn: ({ pageParam = 0 }) => fetchDefectLogs(pageParam, versionId),
//      getNextPageParam: (lastPage) => {
//        const currentPage = lastPage?.data?.number ?? 0;
//        const totalPages = lastPage?.data?.totalPages ?? 1;
//        return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
//      },
//        initialPageParam: 0,
//      enabled: !!versionId,
//    });

// Assigned defects (infinite)
export const useAssignedDefects = (
  params: Omit<QueryParams, "page">,
  options?: { enabled?: boolean },
) =>
  useInfiniteQuery({
    queryKey: ["assignedDefects", params],
    queryFn: ({ pageParam = 0 }) =>
      fetchAssignedDefects({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.data?.number ?? 0;
      const totalPages = lastPage?.data?.totalPages ?? 1;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 0,
    enabled: !!params.versionId && (options?.enabled ?? true),
  });

export function useDefectById(id?: string | number) {
  return useQuery<Defect>({
    queryKey: ["defects", id],
    queryFn: async () => {
      if (!id) throw new Error("no id provided");
      try {
        const res = await fetchDefectById(id);
        const defect = new Defect().deserialize(res);
        return defect;
      } catch (error) {
        console.error("Error in useDefectById:", error);
        throw error;
      }
    },
    enabled: !!id,
    retry: false,
  });
}

export const useExportDefect = (
  params: QueryParams,
  typeFormat: string,
  mode: "all" | "assigned",
) => {
  return useQuery({
    queryKey: ["exportDefect", typeFormat],
    queryFn: () => exportDefect(params, typeFormat, mode),
    enabled: false,
  });
};

export function useLinkedTestCases(defectId?: number) {
  return useQuery({
    queryKey: ["linkedTestCases", defectId],
    queryFn: () => fetchLinkedTestCases(defectId!),
    enabled: !!defectId,
  });
}

export function useDefectFolderTree(versionId?: number) {
  return useQuery({
    queryKey: ["defectFolderTree", versionId],
    queryFn: () => fetchDefectFolderTree(versionId!),
    enabled: !!versionId,
    staleTime: 30_000,
  });
}

export function useDefectReferences(defectId?: number) {
  return useQuery({
    queryKey: ["defectReferences", defectId],
    queryFn: () => fetchDefectReferences(defectId!),
    enabled: !!defectId,
    staleTime: 15_000,
  });
}
